import { Box, Link, Typography, useTheme } from "@mui/material";
import InfoSection from "app/components/InfoSection";
import { parseImageUrl } from "util/parseImageUrl";
import celebrate from "../../../assets/icons/celebrate.svg";
import darklogo from "../../../assets/icons/dark-logo.svg";

import EmailIcon from "../../../assets/icons/email.svg";
import PhoneIcon from "../../../assets/icons/phone.svg";
import WebIcon from "../../../assets/icons/web.svg";

import moment from "moment";
import { formatAddress } from "util/formatAddress";
import CalenderIcon from "../../../assets/icons/black_calender.svg";
import ClockIcon from "../../../assets/icons/clock_black.svg";
import LocationIcon from "../../../assets/icons/location.svg";

import dayjs from "dayjs";
import { useGetBusinessesByUserIdQuery } from "lib/features/business/businessService";
import { Referral } from "lib/features/checkout/checkoutService";
import { useEffect, useState } from "react";
import { successModalStyles as styles } from "styles/Checkout/successModal.styles";

interface SuccessModalProps {
  bookingId?: number | null;
  referral: Referral;
  price: number;
  selectedDate: dayjs.Dayjs | null;
  selectedTime: string;
  endTime: string;
}

interface ReferrerDisplayData {
  name: string;
  avatar?: string;
  isBusiness?: boolean;
}

const SuccessModal = ({
  bookingId,
  referral,
  price,
  selectedDate,
  selectedTime,
  endTime,
}: SuccessModalProps) => {
  const theme = useTheme();
  const [referrerDisplayData, setReferrerDisplayData] = useState<ReferrerDisplayData | null>(null);

  const { data: referrerBusinessData } = useGetBusinessesByUserIdQuery(
    { id: referral?.user?.id ?? null },
    { skip: !referral?.user?.id || referral?.user?.role_id !== 4 }
  );

  useEffect(() => {
    const fetchReferrerDisplayData = async () => {
      if (referral?.user) {
        const isBusiness = referral.user.role_id === 4;
        
        if (isBusiness && referrerBusinessData) {
          const businesses = Array.isArray(referrerBusinessData) 
            ? referrerBusinessData 
            : [referrerBusinessData];
          
          if (businesses.length > 0) {
            const business = businesses[0];
            setReferrerDisplayData({
              name: business?.business || `${referral.user.first_name} ${referral.user.last_name}`,
              avatar: business?.businessImages?.[0]?.uri
                ? parseImageUrl(business.businessImages[0].uri)
                : referral.user.avatar 
                  ? parseImageUrl(referral.user.avatar)
                  : undefined,
              isBusiness: true
            });
          } else {
            setReferrerDisplayData({
              name: `${referral.user.first_name} ${referral.user.last_name}`,
              avatar: referral.user.avatar ? parseImageUrl(referral.user.avatar) : undefined,
              isBusiness: true
            });
          }
        } else {
          setReferrerDisplayData({
            name: `${referral.user.first_name} ${referral.user.last_name[0]}.`,
            avatar: referral.user.avatar ? parseImageUrl(referral.user.avatar) : undefined,
            isBusiness: false
          });
        }
      }
    };

    fetchReferrerDisplayData();
  }, [referral?.user, referrerBusinessData]);

  return (
    <Box sx={styles.container(theme)}>
      {
        bookingId && (
          <Typography
            variant="subtitle1"
            sx={styles.bookingIdText(theme)}
          >
            Booking ID #{bookingId}
          </Typography>
        )
      }

      {/* Icon and message */}
      <Box sx={styles.headerBox}>
        <img
          src={celebrate}
          alt="Success Icon"
        />
        <Typography sx={styles.headerMessage(theme)}>
          Thank you for booking with <br />{referral?.business?.business}!
        </Typography>
      </Box>

      {/* Confirmation message */}
      <Typography sx={styles.confirmationText(theme)}>
        A booking confirmation has been sent to your email!
      </Typography>
      {/* Item Card */}
      <Box sx={styles.itemCard}>
        {/* Title */}
        {referral?.item?.item_type == 1 ? (
          <Typography sx={styles.itemTitle(theme)}>
            {referral?.item?.itemDetails[0]?.item_name || ""}
          </Typography>
        ) : (
          <Typography sx={styles.itemTitle(theme)}>
            {referral?.item?.farehabor_item_details?.name || ""}
          </Typography>
        )}

        {/* Date and Time */}
        <Box sx={styles.dateTimeContainer}>
          <Typography sx={styles.dateTimeText(theme)}>
            <img
              src={CalenderIcon}
              alt="CalenderIcon"
              style={styles.iconStyle}
            />
            {dayjs(selectedDate)?.format("ddd, MMM D, YYYY")}
          </Typography>
          <Typography sx={styles.dateTimeText(theme)}>
            <img
              src={ClockIcon}
              alt="Email Icon"
              style={styles.iconStyle}
            />
            {selectedTime} &#x2022; {moment(endTime).local().format("hh:mm A")}
          </Typography>
        </Box>

        {/* Location */}
        <Box sx={styles.locationBox}>
          <img
            src={LocationIcon}
            alt="Email Icon"
            style={styles.locationIcon}
          />
          <Typography sx={styles.locationText(theme)}>
            {
              formatAddress(referral.item.business?.address1, referral.item.business?.city, referral.item.business?.state)
            }
          </Typography>
        </Box>

        {/* Image */}
        <Box sx={styles.imageContainer}>
          {referral?.item?.item_type == 1 ? (
            <img
              src={
                parseImageUrl(referral?.item?.itemImages[0]?.uri) ||
                "image-placeholder.png"
              }
              alt="Tour Thumbnail"
              style={styles.imageStyle as any}
            />
          ) : (
            <img
              src={
                parseImageUrl(
                  referral?.item?.farehabor_item_details?.images[0]
                    ?.image_cdn_url
                ) || "image-placeholder.png"
              }
              alt="Tour Thumbnail"
              style={styles.imageStyle as any}
            />
          )}
        </Box>
      </Box>

      {/* Description */}
      {referral?.item?.itemDetails[0]?.description && (
        <InfoSection
          title="Description"
          content={referral?.item?.itemDetails[0]?.description}
        />
      )}

      {/* Business Card Info */}
      <Box sx={styles.businessInfoContainer}>
        <Typography sx={styles.businessInfoHeading}>
          Business Contact Info
        </Typography>
        <Box sx={styles.infoRow}>
          <img
            src={EmailIcon}
            alt="Email Icon"
            style={styles.infoIcon}
          />
          {referral?.business?.business_users?.length &&
            referral.business.business_users.length > 0 && (
              <Typography sx={styles.infoText(theme)}>
                {referral.business.business_users[0]?.email ||
                  "No email available"}
              </Typography>
            )}
        </Box>

        <Box sx={styles.infoRow}>
          <img
            src={PhoneIcon}
            alt="Phone Icon"
            style={styles.infoIcon}
          />
          <Typography sx={styles.infoTextAlt(theme)}>
            {referral?.business?.phone}
          </Typography>
        </Box>
        <Box sx={styles.webRow}>
          <img
            src={WebIcon}
            alt="Web Icon"
            style={styles.infoIcon}
          />
          <Typography sx={styles.infoTextAlt(theme)}>
            <Link
              href={`${referral?.business?.address2?.split(".com")[0] + ".com"
                }`}
              target="_blank"
              rel="noopener noreferrer"
              sx={styles.webLink(theme)}
            >
              {referral?.business?.address2?.split(".com")[0] + ".com"}
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Horizontal Line */}
      <Box sx={styles.horizontalLine} />

      <Box sx={styles.totalPriceContainer(theme)}>
        {/* Total Price Label */}
        <Typography sx={styles.totalPriceLabel(theme)}>
          Total Price
        </Typography>

        {/* Total Price Value */}
        <Typography sx={styles.totalPriceValue(theme)}>
          ${price && price?.toFixed(2)}
        </Typography>
      </Box>

      {/* Horizontal Line */}
      <Box sx={styles.horizontalLine} />

      {/* Referred By */}
      <Box>
        <Typography sx={styles.referredByHeading}>
          Referred by
        </Typography>

        <Box sx={styles.referrerContainer}>
          <img
            src={
              referrerDisplayData?.avatar || "/default-avatar.png"
            }
            alt={referrerDisplayData?.isBusiness ? `business_avatar` : `user_avatar`}
            style={styles.referrerAvatar as any}
          />

          {/* Referrer Name */}
          <Typography sx={styles.referrerName}>
            {referrerDisplayData?.name || "Loading..."}
          </Typography>
        </Box>

        {/* Referrer Message */}
        <Typography sx={styles.referrerMessage}>
          {referral?.additional_comments ? (
            <>
              {`"${referral.additional_comments}"`}
              <br />
            </>
          ) : null}
        </Typography>
      </Box>

      {/* powered by */}
      <Box sx={styles.footerContainer}>
        <Typography
          sx={styles.footerText(theme)}
        >
          Powered by
        </Typography>
        <img
          src={darklogo}
          alt="Success Icon"
          style={styles.footerLogo}
        />
      </Box>
    </Box>
  );
};

export default SuccessModal;